import type * as React from 'react';
import Box from '@cloudscape-design/components/box';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Link from '@cloudscape-design/components/link';
import Popover from '@cloudscape-design/components/popover';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import { InvoiceStatus, nfsePdfUrl, type Invoice, type NfseStatus } from '@etiquetachef/types';
import { formatDate } from '@/lib/format';
import { InvoiceStatusBadge } from '@/components/InvoiceStatusBadge';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { isActionVisible } from '@/components/invoiceActionVisibility';

/**
 * Ações que a tabela pode emitir por linha. O caller decide o que cada uma faz
 * (navegar, abrir diálogo, abrir PDF); a tabela só filtra quais são visíveis
 * (`isActionVisible`).
 */
export type InvoiceTableAction =
  | 'view'
  | 'download_pdf'
  | 'download_nfe'
  | 'pay_now'
  | 'mark_paid'
  | 'cancel'
  | 'resend_email';

export type InvoiceTableLabels = {
  invoiceNumber: string;
  issuedAt: string;
  dueAt: string;
  total: string;
  status: string;
  actions: string;
  empty: string;
  view: string;
  downloadPdf: string;
  downloadNfe?: string;
  /**
   * #754 (2026-09-27 22:33 +0200) — coluna "NFS-e" (status da nota do Asaas, com o motivo do
   * erro no `title`). Só aparece quando o caller passa o cabeçalho e os
   * rótulos de status — o admin passa; faturas sem nota mostram "—".
   */
  nfse?: string;
  nfseStatus?: Record<NfseStatus, string>;
  payNow: string;
  markPaid: string;
  cancel: string;
  resendEmail: string;
};

type Props = {
  invoices: Invoice[];
  locale: string;
  tone: 'admin' | 'client';
  labels: InvoiceTableLabels;
  onRowAction?: (invoice: Invoice, action: InvoiceTableAction) => void;
};

// `tone: 'client'` é herança da interface do ui-shared; no admin só
// `InvoicesPage` usa a tabela, sempre com `tone="admin"` — `CLIENT_ACTIONS`
// não é exercitado aqui (ver #796).
const CLIENT_ACTIONS: InvoiceTableAction[] = ['view', 'download_pdf', 'download_nfe', 'pay_now'];
const ADMIN_ACTIONS: InvoiceTableAction[] = [
  'view',
  'download_pdf',
  'download_nfe',
  'pay_now',
  'mark_paid',
  'resend_email',
  'cancel',
];

/** Rótulo i18n da ação; "Baixar NFS-e" cai no rótulo do PDF se o caller não passou o seu. */
function actionLabel(action: InvoiceTableAction, labels: InvoiceTableLabels): string {
  switch (action) {
    case 'view':
      return labels.view;
    case 'download_pdf':
      return labels.downloadPdf;
    case 'download_nfe':
      return labels.downloadNfe ?? labels.downloadPdf;
    case 'pay_now':
      return labels.payNow;
    case 'mark_paid':
      return labels.markPaid;
    case 'cancel':
      return labels.cancel;
    case 'resend_email':
      return labels.resendEmail;
  }
}

/**
 * Tabela de faturas do admin (Cloudscape `Table` + `ButtonDropdown`).
 *
 * Origem: sem issue — commit 29c36b104, 2026-06-09 19:05 +0200 (billing do
 * admin em Cloudscape; substituiu o `InvoiceTable` Tailwind do ui-shared, que
 * já não existe — o admin bane `@etiquetachef/ui-shared`).
 *
 * Contrato: só apresentação — recebe `Invoice[]` já lidos pelo caller
 * (`subscribeTenantInvoices`, limite de 100). Mesmas props/ações da interface
 * herdada do ui-shared, MAIS a coluna opcional "NFS-e" (`labels.nfse` +
 * `labels.nfseStatus`, #754), que só aparece quando o caller passa os dois.
 * Número da fatura clicável emite `'view'`; sem `onRowAction` não há coluna
 * de ações nem link. `'download_nfe'` só aparece com `nfsePdfUrl(inv.nfse)`
 * resolvido E `labels.downloadNfe` definido.
 *
 * Usado por: `routes/InvoicesPage.tsx` (rotas `/billing/invoices` e
 * `/billing/invoices/:tenantId`, menu Cobrança → Faturas; spec
 * `docs/screens/billing-invoices.md`), admin web — sempre com `tone="admin"`.
 *
 * Dados: `Invoice` (`InvoiceSchema`, `packages/types/src/schemas.ts`), doc
 * `tenants/{tenantId}/invoices/{invoiceId}`. `total_amount`/`amount_due` em
 * CENTAVOS — `MoneyDisplay` divide por 100. `issued_at`/`due_at` epoch ms.
 * `nfse` (opcional; só fatura do Asaas, #754): `{ status, error?, pdf_url? }` —
 * `error` é o motivo da recusa da prefeitura, texto livre do provedor.
 *
 * Falhas conhecidas:
 *  - 2026-09-28 (#754, auditoria B8): o motivo do erro da NFS-e ficava num
 *    `title`, invisível a teclado e leitor de tela — hoje `Popover`.
 *
 * Histórico:
 *  - 2026-06-10 00:41 +0200 — número da fatura clicável abre o detalhe.
 *  - 2026-09-27 22:44 +0200 — (#754) coluna NFS-e e ação "Baixar NFS-e"
 *    (`nfsePdfUrl`).
 *  - 2026-09-28 08:07 +0200 — (#754) motivo do erro em `Popover`.
 *  - 2026-10-10 14:40 +0200 — (#798) rotas, menu e spec (#796).
 *  - 2026-10-13 10:28 +0200 — (#935, item 7) "Cancelar" só em PENDING.
 */
export function InvoiceTable({
  invoices,
  locale,
  tone,
  labels,
  onRowAction,
}: Props): React.JSX.Element {
  const actions = tone === 'admin' ? ADMIN_ACTIONS : CLIENT_ACTIONS;

  const columns: TableProps.ColumnDefinition<Invoice>[] = [
    {
      id: 'invoiceNumber',
      header: labels.invoiceNumber,
      cell: (inv): React.ReactNode => {
        const number = (
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{inv.invoice_number}</span>
        );
        // Número clicável abre o detalhe — reusa a ação 'view' que o caller já
        // conecta à navegação. Sem onRowAction não há destino: cai no texto.
        return onRowAction ? (
          <Link
            variant="primary"
            onFollow={(e): void => {
              e.preventDefault();
              onRowAction(inv, 'view');
            }}
          >
            {number}
          </Link>
        ) : (
          number
        );
      },
    },
    {
      id: 'issuedAt',
      header: labels.issuedAt,
      cell: (inv): React.ReactNode => formatDate(inv.issued_at, locale),
    },
    {
      id: 'dueAt',
      header: labels.dueAt,
      cell: (inv): React.ReactNode => formatDate(inv.due_at, locale),
    },
    {
      id: 'total',
      header: labels.total,
      cell: (inv): React.ReactNode => (
        <MoneyDisplay amount={inv.total_amount} currency={inv.currency} locale={locale} />
      ),
    },
    {
      id: 'status',
      header: labels.status,
      cell: (inv): React.ReactNode => <InvoiceStatusBadge status={inv.status} />,
    },
  ];

  const { nfse: nfseHeader, nfseStatus } = labels;
  if (nfseHeader !== undefined && nfseStatus !== undefined) {
    columns.push({
      id: 'nfse',
      header: nfseHeader,
      cell: (inv): React.ReactNode =>
        !inv.nfse ? (
          '—'
        ) : inv.nfse.error ? (
          // Auditoria #754 (B8): o motivo ficava num `title`, invisível a
          // teclado e leitor de tela. O `Popover` abre por clique/foco.
          <Popover content={inv.nfse.error} dismissButton={false} triggerType="text">
            {nfseStatus[inv.nfse.status]}
          </Popover>
        ) : (
          nfseStatus[inv.nfse.status]
        ),
    });
  }

  if (onRowAction) {
    columns.push({
      id: 'actions',
      header: labels.actions,
      cell: (inv): React.ReactNode => {
        const items = actions
          .filter((action) =>
            isActionVisible(action, {
              canPay:
                inv.status !== InvoiceStatus.PAID &&
                inv.status !== InvoiceStatus.CANCELLED &&
                inv.status !== InvoiceStatus.REFUNDED,
              isPending: inv.status === InvoiceStatus.PENDING,
              hasPdf: inv.pdf_url !== null,
              hasNfe: nfsePdfUrl(inv.nfse) !== null,
              hasNfeLabel: labels.downloadNfe !== undefined,
            }),
          )
          .map((action) => ({ id: action, text: actionLabel(action, labels) }));
        if (items.length === 0) return null;
        return (
          <ButtonDropdown
            items={items}
            expandToViewport
            onItemClick={({ detail }): void => {
              onRowAction(inv, detail.id as InvoiceTableAction);
            }}
          >
            {labels.actions}
          </ButtonDropdown>
        );
      },
    });
  }

  return (
    <Table<Invoice>
      items={invoices}
      trackBy="id"
      variant="container"
      columnDefinitions={columns}
      empty={
        <Box textAlign="center" color="inherit">
          {labels.empty}
        </Box>
      }
    />
  );
}
